// ============================================================================
// KLINISOL CRO - Form Portal Component
// Main entry point for form automation system
// WITH HELP MODAL
// COLOR SCHEME: Slate Grey (#475569) & Magenta (#E91E63)
// ============================================================================

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
    FileText, 
    Plus, 
    Clock, 
    CheckCircle, 
    AlertCircle,
    Search,
    Filter,
    Eye,
    Edit,
    Trash2,
    Send,
    Loader2,
    HelpCircle,
    X,
    ArrowLeft,
    ClipboardList,
    UserCheck,
    GitBranch,
    Zap
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import formApi from '../services/formApi';

const FormPortal = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    
    const [forms, setForms] = useState([]);
    const [schemas, setSchemas] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [selectedForm, setSelectedForm] = useState(null);
    const [showHelp, setShowHelp] = useState(false);

    useEffect(() => {
        loadData();
    }, [statusFilter]);

    const loadData = async () => {
        setLoading(true);
        setError('');
        
        try {
            // Load schemas and submissions in parallel
            const [schemasData, formsData] = await Promise.all([
                formApi.getAllSchemas({ status: 'active' }),
                formApi.getUserSubmissions({ 
                    status: statusFilter === 'all' ? null : statusFilter 
                })
            ]);
            
            setSchemas(schemasData);
            setForms(formsData);
        } catch (err) {
            console.error('Error loading data:', err);
            setError('Failed to load forms. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleCreateNew = (schemaId) => {
        navigate(`/forms/create/${schemaId}`);
    };

    const handleViewForm = (formId) => {
        navigate(`/forms/view/${formId}`);
    };

    const handleEditForm = (formId) => {
        navigate(`/forms/edit/${formId}`);
    };

    const handleDeleteForm = async (formId) => {
        if (!window.confirm('Are you sure you want to delete this draft form?')) {
            return;
        }
        
        try {
            await formApi.deleteSubmission(formId);
            loadData();
        } catch (err) {
            console.error('Error deleting form:', err);
            alert('Failed to delete form. Please try again.');
        }
    };

    const handleSubmitForm = async (formId) => {
        if (!window.confirm('Submit this form for approval? You will not be able to edit it after submission.')) {
            return;
        }
        
        try {
            await formApi.submitForApproval(formId);
            alert('Form submitted successfully!');
            loadData();
        } catch (err) {
            console.error('Error submitting form:', err);
            alert('Failed to submit form. Please try again.');
        }
    };

    const getStatusBadge = (status) => {
        const styles = {
            draft: 'bg-gray-100 text-gray-800 border-gray-300',
            submitted: 'bg-blue-100 text-blue-800 border-blue-300',
            in_review: 'bg-orange-100 text-orange-800 border-orange-300',
            approved: 'bg-green-100 text-green-800 border-green-300',
            rejected: 'bg-red-100 text-red-800 border-red-300'
        };
        
        const icons = {
            draft: <Edit className="w-3.5 h-3.5" />,
            submitted: <Send className="w-3.5 h-3.5" />,
            in_review: <Clock className="w-3.5 h-3.5" />,
            approved: <CheckCircle className="w-3.5 h-3.5" />,
            rejected: <AlertCircle className="w-3.5 h-3.5" />
        };
        
        return (
            <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold border-2 ${styles[status]}`}>
                {icons[status]}
                {status.replace('_', ' ').toUpperCase()}
            </span>
        );
    };

    const filteredForms = forms.filter(form => 
        searchTerm === '' || 
        form.document_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
        form.title.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // ========================================================================
    // HELP MODAL
    // ========================================================================

    const HelpModal = () => {
        if (!showHelp) return null;

        return (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                    {/* Header */}
                    <div className="sticky top-0 bg-gradient-to-r from-[#475569] to-[#E91E63] text-white px-6 py-4 flex items-center justify-between rounded-t-xl">
                        <div className="flex items-center gap-3">
                            <ClipboardList className="w-6 h-6" />
                            <h3 className="font-bold text-xl">Form Portal Guide</h3>
                        </div>
                        <button 
                            onClick={() => setShowHelp(false)}
                            className="hover:bg-white/20 rounded-lg p-2 transition-all"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>

                    {/* Content */}
                    <div className="p-6 space-y-6">
                        
                        {/* What is Form Portal */}
                        <section>
                            <h4 className="font-bold text-gray-900 text-lg mb-3">What is Form Portal?</h4>
                            <p className="text-gray-700 mb-3">
                                The Form Portal is your <strong className="text-[#E91E63]">digital forms hub</strong> - 
                                create, submit, and track clinical research forms with automated workflows.
                            </p>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                                    <div className="font-bold text-blue-900 mb-1 flex items-center gap-2">
                                        <Zap className="w-4 h-4" />
                                        Auto-Fill
                                    </div>
                                    <div className="text-xs text-blue-800">80% of fields auto-populated from your profile and system data</div>
                                </div>
                                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                                    <div className="font-bold text-green-900 mb-1 flex items-center gap-2">
                                        <GitBranch className="w-4 h-4" />
                                        Workflow
                                    </div>
                                    <div className="text-xs text-green-800">Automatic routing for review and approval</div>
                                </div>
                                <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                                    <div className="font-bold text-purple-900 mb-1 flex items-center gap-2">
                                        <UserCheck className="w-4 h-4" />
                                        E-Signatures
                                    </div>
                                    <div className="text-xs text-purple-800">Electronic signatures with full audit trail</div>
                                </div>
                            </div>
                        </section>

                        {/* Form Status */}
                        <section>
                            <h4 className="font-bold text-gray-900 text-lg mb-3">Form Status Explained</h4>
                            <div className="space-y-2">
                                <div className="flex items-center gap-3 text-sm">
                                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold border-2 bg-gray-100 text-gray-800 border-gray-300">
                                        <Edit className="w-3.5 h-3.5" /> DRAFT
                                    </span>
                                    <span>Work in progress - you can still edit</span>
                                </div>
                                <div className="flex items-center gap-3 text-sm">
                                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold border-2 bg-blue-100 text-blue-800 border-blue-300">
                                        <Send className="w-3.5 h-3.5" /> SUBMITTED
                                    </span>
                                    <span>Sent for review - no more edits</span>
                                </div>
                                <div className="flex items-center gap-3 text-sm">
                                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold border-2 bg-orange-100 text-orange-800 border-orange-300">
                                        <Clock className="w-3.5 h-3.5" /> IN REVIEW
                                    </span>
                                    <span>Being reviewed by approvers</span>
                                </div>
                                <div className="flex items-center gap-3 text-sm">
                                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold border-2 bg-green-100 text-green-800 border-green-300">
                                        <CheckCircle className="w-3.5 h-3.5" /> APPROVED
                                    </span>
                                    <span>Completed and approved</span>
                                </div>
                                <div className="flex items-center gap-3 text-sm">
                                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold border-2 bg-red-100 text-red-800 border-red-300">
                                        <AlertCircle className="w-3.5 h-3.5" /> REJECTED
                                    </span>
                                    <span>Returned for corrections</span>
                                </div>
                            </div>
                        </section>

                        {/* How to Use */}
                        <section>
                            <h4 className="font-bold text-gray-900 text-lg mb-3">How to Use</h4>
                            <ol className="space-y-2 text-gray-700">
                                <li className="flex gap-3">
                                    <span className="font-bold text-[#E91E63] flex-shrink-0">1.</span>
                                    <span>Find the form you need in <strong>"Available Forms"</strong></span>
                                </li>
                                <li className="flex gap-3">
                                    <span className="font-bold text-[#E91E63] flex-shrink-0">2.</span>
                                    <span>Click <strong>"Create New"</strong> to start a new form</span>
                                </li>
                                <li className="flex gap-3">
                                    <span className="font-bold text-[#E91E63] flex-shrink-0">3.</span>
                                    <span>Fill in required fields (many are auto-populated)</span>
                                </li>
                                <li className="flex gap-3">
                                    <span className="font-bold text-[#E91E63] flex-shrink-0">4.</span>
                                    <span>Save as draft or submit for approval</span>
                                </li>
                                <li className="flex gap-3">
                                    <span className="font-bold text-[#E91E63] flex-shrink-0">5.</span>
                                    <span>Track status in <strong>"My Forms"</strong> section</span>
                                </li>
                            </ol>
                        </section>

                        {/* Action Buttons */}
                        <section>
                            <h4 className="font-bold text-gray-900 text-lg mb-3">Action Buttons</h4>
                            <div className="grid grid-cols-2 gap-3">
                                <div className="flex items-center gap-2 text-sm">
                                    <Eye className="w-5 h-5 text-gray-600" />
                                    <span><strong>View</strong> - Read-only preview</span>
                                </div>
                                <div className="flex items-center gap-2 text-sm">
                                    <Edit className="w-5 h-5 text-blue-600" />
                                    <span><strong>Edit</strong> - Modify draft</span>
                                </div>
                                <div className="flex items-center gap-2 text-sm">
                                    <Send className="w-5 h-5 text-green-600" />
                                    <span><strong>Submit</strong> - Send for approval</span>
                                </div>
                                <div className="flex items-center gap-2 text-sm">
                                    <Trash2 className="w-5 h-5 text-red-600" />
                                    <span><strong>Delete</strong> - Remove draft</span>
                                </div>
                            </div>
                        </section>

                        {/* Tips */}
                        <section>
                            <h4 className="font-bold text-gray-900 text-lg mb-3">💡 Pro Tips</h4>
                            <ul className="space-y-2 text-sm text-gray-700">
                                <li className="flex gap-2">
                                    <span className="text-[#E91E63]">•</span>
                                    <span>Save frequently - drafts are auto-saved but manual saves ensure nothing is lost</span>
                                </li>
                                <li className="flex gap-2">
                                    <span className="text-[#E91E63]">•</span>
                                    <span>Review all auto-populated fields - verify data is correct before submitting</span>
                                </li>
                                <li className="flex gap-2">
                                    <span className="text-[#E91E63]">•</span>
                                    <span>Use filters to find forms quickly by status</span>
                                </li>
                                <li className="flex gap-2">
                                    <span className="text-[#E91E63]">•</span>
                                    <span>Once submitted, forms cannot be edited - create a new version if changes needed</span>
                                </li>
                            </ul>
                        </section>

                        {/* FDA Compliance */}
                        <section className="bg-green-50 border border-green-200 rounded-lg p-4">
                            <h4 className="font-bold text-green-900 mb-2 flex items-center gap-2">
                                <span>🛡️</span>
                                <span>Designed for FDA 21 CFR Part 11</span>
                            </h4>
                            <p className="text-sm text-green-800">
                                All form submissions include electronic signatures, timestamps, and complete 
                                audit trails. System designed with 21 CFR Part 11 principles. Formal validation pending.
                            </p>
                        </section>

                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="min-h-screen bg-[#f8fafd]">
            {/* Help Modal */}
            <HelpModal />

            {/* Header */}
            <div className="bg-gradient-to-r from-[#475569] via-[#E91E63] to-[#475569] text-white py-6 px-6 shadow-md">
                <div className="max-w-7xl mx-auto flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <button
                            onClick={() => navigate('/dashboard')}
                            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                        >
                            <ArrowLeft className="w-6 h-6" />
                        </button>
                        <div>
                            <h1 className="text-3xl font-bold">Form Automation Portal</h1>
                            <p className="text-sm opacity-90">
                                Designed for FDA 21 CFR Part 11 | 80% Auto-Population
                            </p>
                        </div>
                    </div>
                    
                    <button
                        onClick={() => setShowHelp(true)}
                        className="px-4 py-2 border border-white/30 hover:bg-white/10 rounded-lg transition-colors flex items-center gap-2"
                    >
                        <HelpCircle className="w-4 h-4" />
                        Help
                    </button>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-6 py-8">
                {/* Error Message */}
                {error && (
                    <div className="bg-red-50 border-2 border-red-500 text-red-900 px-6 py-4 rounded-xl mb-6 flex items-start gap-3">
                        <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                        <div>
                            <p className="font-bold mb-1">Error</p>
                            <p className="text-sm">{error}</p>
                        </div>
                    </div>
                )}

                {/* Available Forms Section */}
                <section className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 mb-8">
                    <h2 className="text-2xl font-bold text-[#475569] mb-6">
                        Available Forms
                    </h2>
                    
                    {loading ? (
                        <div className="flex items-center justify-center py-12">
                            <Loader2 className="w-8 h-8 animate-spin text-[#475569]" />
                        </div>
                    ) : schemas.length === 0 ? (
                        <div className="text-center py-12 text-gray-500">
                            <FileText className="w-16 h-16 mx-auto mb-4 opacity-50" />
                            <p>No forms available yet.</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {schemas.map(schema => (
                                <div
                                    key={schema.id}
                                    className="group bg-gradient-to-br from-[#f8fafc] to-[#f1f5f9] border-2 border-gray-200 rounded-xl p-6 hover:shadow-lg hover:border-[#E91E63]/50 transition-all"
                                >
                                    <div className="flex items-start gap-4 mb-4">
                                        <div className="w-12 h-12 bg-gradient-to-br from-[#475569] to-[#334155] rounded-xl flex items-center justify-center flex-shrink-0">
                                            <FileText className="w-6 h-6 text-white" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <h3 className="font-bold text-[#475569] mb-1 truncate">
                                                {schema.document_number}
                                            </h3>
                                            <p className="text-sm text-gray-600 line-clamp-2">
                                                {schema.title}
                                            </p>
                                        </div>
                                    </div>
                                    
                                    <button
                                        onClick={() => handleCreateNew(schema.id)}
                                        className="w-full py-3 bg-gradient-to-r from-[#475569] to-[#334155] text-white rounded-lg font-semibold hover:from-[#334155] hover:to-[#1e293b] transition-all flex items-center justify-center gap-2 group-hover:shadow-md"
                                    >
                                        <Plus className="w-4 h-4" />
                                        Create New
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </section>

                {/* My Forms Section */}
                <section className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
                    <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
                        <h2 className="text-2xl font-bold text-[#475569]">
                            My Forms
                        </h2>
                        
                        <div className="flex flex-wrap items-center gap-3">
                            {/* Search */}
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                <input
                                    type="text"
                                    placeholder="Search forms..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="pl-10 pr-4 py-2 border-2 border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#475569] transition-all"
                                />
                            </div>
                            
                            {/* Filter */}
                            <div className="relative">
                                <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                <select
                                    value={statusFilter}
                                    onChange={(e) => setStatusFilter(e.target.value)}
                                    className="pl-10 pr-8 py-2 border-2 border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#475569] transition-all appearance-none bg-white"
                                >
                                    <option value="all">All Status</option>
                                    <option value="draft">Draft</option>
                                    <option value="submitted">Submitted</option>
                                    <option value="in_review">In Review</option>
                                    <option value="approved">Approved</option>
                                    <option value="rejected">Rejected</option>
                                </select>
                            </div>
                        </div>
                    </div>
                    
                    {loading ? (
                        <div className="flex items-center justify-center py-12">
                            <Loader2 className="w-8 h-8 animate-spin text-[#475569]" />
                        </div>
                    ) : filteredForms.length === 0 ? (
                        <div className="text-center py-12 text-gray-500">
                            <FileText className="w-16 h-16 mx-auto mb-4 opacity-50" />
                            <p className="mb-2">No forms found.</p>
                            <p className="text-sm">Create your first form to get started!</p>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {filteredForms.map(form => (
                                <div
                                    key={form.id}
                                    className="border-2 border-gray-200 rounded-xl p-5 hover:border-[#E91E63]/50 hover:shadow-md transition-all"
                                >
                                    <div className="flex items-start justify-between gap-4">
                                        <div className="flex-1">
                                            <div className="flex items-center gap-3 mb-2">
                                                <h3 className="font-bold text-gray-900">
                                                    {form.document_number}
                                                </h3>
                                                {getStatusBadge(form.status)}
                                            </div>
                                            <p className="text-sm text-gray-600 mb-3">
                                                {form.title}
                                            </p>
                                            <div className="flex flex-wrap items-center gap-4 text-xs text-gray-500">
                                                <span>Created: {new Date(form.created_at).toLocaleDateString()}</span>
                                                {form.submitted_at && (
                                                    <span>Submitted: {new Date(form.submitted_at).toLocaleDateString()}</span>
                                                )}
                                                {form.total_steps > 0 && (
                                                    <span>
                                                        Workflow: {form.completed_steps}/{form.total_steps} steps
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                        
                                        <div className="flex items-center gap-2">
                                            <button
                                                onClick={() => handleViewForm(form.id)}
                                                className="p-2 hover:bg-gray-100 rounded-lg transition-all"
                                                title="View"
                                            >
                                                <Eye className="w-5 h-5 text-gray-600" />
                                            </button>
                                            
                                            {form.status === 'draft' && (
                                                <>
                                                    <button
                                                        onClick={() => handleEditForm(form.id)}
                                                        className="p-2 hover:bg-blue-50 rounded-lg transition-all"
                                                        title="Edit"
                                                    >
                                                        <Edit className="w-5 h-5 text-blue-600" />
                                                    </button>
                                                    <button
                                                        onClick={() => handleSubmitForm(form.id)}
                                                        className="p-2 hover:bg-green-50 rounded-lg transition-all"
                                                        title="Submit"
                                                    >
                                                        <Send className="w-5 h-5 text-green-600" />
                                                    </button>
                                                    <button
                                                        onClick={() => handleDeleteForm(form.id)}
                                                        className="p-2 hover:bg-red-50 rounded-lg transition-all"
                                                        title="Delete"
                                                    >
                                                        <Trash2 className="w-5 h-5 text-red-600" />
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </section>
            </div>
        </div>
    );
};

export default FormPortal;
